import { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, Eye } from 'lucide-react'
import { useAudio } from '../../hooks/useAudio'
import { useSparkle } from '../../hooks/useSparkle'

interface NumberRecognitionProps {
  targetNumber: number
  onComplete: (correct: boolean) => void
}

// Compact visual representations
const NUMBER_PATTERNS: Record<number, string[]> = {
  1: ['1️⃣', '☝️'],
  2: ['2️⃣', '✌️'],
  3: ['3️⃣', '👌'],
  4: ['4️⃣', '🦋🦋🦋🦋'],
  5: ['5️⃣', '🖐️'],
  6: ['6️⃣', '👍👍👍'],
  7: ['7️⃣', '🎈🎈🎈🎈🎈🎈🎈'],
  8: ['8️⃣', '⭐⭐⭐⭐⭐⭐⭐⭐'],
  9: ['9️⃣', '🍎🍎🍎🍎🍎🍎🍎🍎🍎'],
  10: ['🔟', '🙌'],
}

export default function NumberRecognition({ targetNumber, onComplete }: NumberRecognitionProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)
  const [countdown, setCountdown] = useState(0)
  
  const { playCorrect, playWrong } = useAudio()
  const { miniBurst } = useSparkle()

  // Get representations for target and nearby numbers
  const nearbyNumbers = [
    Math.max(1, targetNumber - 2),
    Math.max(1, targetNumber - 1),
    targetNumber,
    Math.min(10, targetNumber + 1),
  ].filter((n, i, arr) => arr.indexOf(n) === i)

  // Pick one random representation for each number
  const options = nearbyNumbers.map(num => {
    const patterns = NUMBER_PATTERNS[num]
    return {
      number: num,
      display: patterns[Math.floor(Math.random() * patterns.length)],
    }
  }).sort(() => Math.random() - 0.5)

  const handleSelect = useCallback((number: number) => {
    if (showFeedback) return

    const correct = number === targetNumber
    setSelectedOption(String(number))
    setIsCorrect(correct)
    setShowFeedback(true)
    
    // Play sound and effects
    if (correct) {
      playCorrect()
      miniBurst(0.5, 0.5)
    } else {
      playWrong()
    }
    
    // Differentiated timing: wrong answers get more time
    const feedbackDuration = correct ? 1200 : 2000
    setCountdown(Math.ceil(feedbackDuration / 1000))

    setTimeout(() => {
      onComplete(correct)
    }, feedbackDuration)
  }, [onComplete, showFeedback, targetNumber, playCorrect, playWrong, miniBurst])
  
  // Countdown timer for visual feedback
  useEffect(() => {
    if (!showFeedback || countdown <= 0) return
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    
    return () => clearInterval(timer)
  }, [showFeedback, countdown])

  return (
    <div className="w-full max-w-sm mx-auto px-3">
      {/* Compact Header */}
      <motion.div 
        className="text-center mb-4"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="inline-flex items-center gap-1.5 bg-[var(--math-primary)]/10 px-3 py-1.5 rounded-full mb-2">
          <Eye className="w-4 h-4 text-[var(--math-primary)]" />
          <span className="text-xs font-medium text-[var(--math-primary)]">Find the number</span>
        </div>
        <div className="text-4xl font-bold text-[var(--math-primary)]">{targetNumber}</div>
      </motion.div>

      {/* Options - 2x2 Grid */}
      <div className="grid grid-cols-2 gap-3">
        {options.map((option, index) => {
          const isSelected = selectedOption === String(option.number)
          const showAsCorrect = showFeedback && option.number === targetNumber
          const showAsWrong = showFeedback && isSelected && option.number !== targetNumber

          return (
            <motion.button
              key={option.number}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.2 }}
              whileHover={!showFeedback ? { scale: 1.03 } : {}}
              whileTap={!showFeedback ? { scale: 0.97 } : {}}
              onClick={() => handleSelect(option.number)}
              disabled={showFeedback}
              className={`
                h-20 sm:h-22 rounded-2xl
                flex flex-col items-center justify-center gap-1
                transition-all duration-150
                ${showAsCorrect 
                  ? 'bg-[var(--math-success)] text-white shadow-lg' 
                  : showAsWrong 
                    ? 'bg-[var(--math-error)] text-white'
                    : isSelected 
                      ? 'bg-[var(--math-primary)] text-white shadow-md'
                      : 'bg-white shadow-sm hover:shadow-md border-2 border-gray-100'
                }
              `}
            >
              <span className="text-3xl">{option.display}</span>
              <span className="text-xs opacity-70">= {option.number}</span>
            </motion.button>
          )
        })}
      </div>

      {/* Compact Feedback */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-center mt-3"
          >
            {isCorrect ? (
              <div className="flex items-center justify-center gap-2 bg-[var(--math-success)]/10 px-4 py-2 rounded-full">
                <Sparkles className="w-5 h-5 text-[var(--math-success)]" />
                <span className="font-bold text-[var(--math-success)]">You found {targetNumber}!</span>
                {countdown > 0 && (
                  <span className="text-xs text-[var(--math-success)]/60 ml-1">{countdown}</span>
                )}
              </div>
            ) : (
              <div className="bg-[var(--math-error)]/10 px-3 py-2 rounded-xl">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-gray-500">Look for:</span>
                  <span className="font-bold text-[var(--math-success)] text-2xl">{targetNumber}</span>
                </div>
                <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden max-w-[120px] mx-auto">
                  <motion.div
                    className="h-full bg-[var(--math-error)]"
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: 2, ease: 'linear' }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
